import { useState, useEffect } from "react";
import { Video } from "@/entities/Video";
import { Play, Search, ChevronDown, Film } from "lucide-react";

const CATEGORIAS = ["todas", "musica", "entretenimiento", "noticias", "deportes", "educacion", "otro"];

function getEmbedUrl(video) {
  if (!video?.url_video) return null;
  if (video.tipo_video === "youtube") {
    const match = video.url_video.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
    if (match) return `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0`;
    if (video.url_video.includes("youtube.com/embed")) return video.url_video + "?autoplay=1";
    return video.url_video;
  }
  return video.url_video;
}

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoActivo, setVideoActivo] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [categoria, setCategoria] = useState("todas");

  useEffect(() => {
    async function cargar() {
      const data = await Video.filter({ activo: true }, "orden", 100);
      setVideos(data);
      if (data.length > 0) {
        const urlParams = new URLSearchParams(window.location.search);
        const vid = urlParams.get("video");
        const found = vid ? data.find(v => v.id === vid) : null;
        setVideoActivo(found || data[0]);
      }
      setLoading(false);
    }
    cargar();
  }, []);

  const filtrados = videos.filter(v => {
    const matchB = v.titulo?.toLowerCase().includes(busqueda.toLowerCase()) ||
      v.descripcion?.toLowerCase().includes(busqueda.toLowerCase());
    const matchC = categoria === "todas" || v.categoria === categoria;
    return matchB && matchC;
  });

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="border-b border-yellow-500/20 px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-yellow-500/20 flex items-center justify-center">
            <Play className="w-4 h-4 text-yellow-400" />
          </div>
          <h1 className="text-xl font-bold text-white">Videos</h1>
          <span className="text-gray-500 text-sm">{videos.length} videos</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-20 text-gray-600">
            <Film className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <p className="text-lg">No hay videos disponibles</p>
            <p className="text-sm mt-1">Agrega videos desde el panel de administración</p>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Player */}
            <div className="flex-1">
              {videoActivo && (
                <div>
                  <div className="relative w-full bg-gray-950 rounded-2xl overflow-hidden border border-yellow-500/20"
                    style={{ paddingTop: "56.25%" }}>
                    {videoActivo.tipo_video === "mp4" ? (
                      <video
                        key={videoActivo.id}
                        src={videoActivo.url_video}
                        controls
                        autoPlay
                        className="absolute inset-0 w-full h-full object-contain bg-black"
                      />
                    ) : (
                      <iframe
                        key={videoActivo.id}
                        src={getEmbedUrl(videoActivo)}
                        className="absolute inset-0 w-full h-full"
                        allowFullScreen
                        allow="autoplay; encrypted-media; picture-in-picture"
                        title={videoActivo.titulo}
                      />
                    )}
                  </div>
                  <div className="mt-3 px-1">
                    <h2 className="text-lg font-bold text-white">{videoActivo.titulo}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs bg-gray-800 text-gray-400 px-2 py-0.5 rounded-full capitalize">
                        {videoActivo.categoria}
                      </span>
                      {videoActivo.duracion && (
                        <span className="text-xs text-gray-500">{videoActivo.duracion}</span>
                      )}
                    </div>
                    {videoActivo.descripcion && (
                      <p className="text-gray-500 text-sm mt-2">{videoActivo.descripcion}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Lista */}
            <div className="lg:w-80 xl:w-96 flex flex-col gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Buscar video..."
                  value={busqueda}
                  onChange={e => setBusqueda(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-800 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-yellow-500/50"
                />
              </div>
              <div className="relative">
                <select
                  value={categoria}
                  onChange={e => setCategoria(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-800 rounded-xl px-4 py-2.5 text-sm text-white appearance-none focus:outline-none focus:border-yellow-500/50"
                >
                  {CATEGORIAS.map(c => (
                    <option key={c} value={c}>{c === "todas" ? "Todas las categorías" : c}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
              </div>

              <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto pr-1">
                {filtrados.length === 0 ? (
                  <p className="text-center text-gray-600 py-6 text-sm">Sin resultados</p>
                ) : filtrados.map(video => (
                  <button
                    key={video.id}
                    onClick={() => setVideoActivo(video)}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left w-full ${
                      videoActivo?.id === video.id
                        ? "border-yellow-500/60 bg-yellow-500/10"
                        : "border-gray-800 bg-gray-900 hover:border-gray-700 hover:bg-gray-800"
                    }`}
                  >
                    <div className="w-20 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-800 flex items-center justify-center relative">
                      {video.thumbnail ? (
                        <img src={video.thumbnail} alt={video.titulo} className="w-full h-full object-cover" />
                      ) : (
                        <Film className="w-5 h-5 text-gray-600" />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <Play className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold text-sm truncate ${videoActivo?.id === video.id ? "text-yellow-400" : "text-white"}`}>
                        {video.titulo}
                      </p>
                      <p className="text-gray-600 text-xs capitalize">{video.categoria} {video.duracion ? `· ${video.duracion}` : ""}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}